// ANGEL FLIGHT — delta-time timer helpers.
//
// Two tiny stateful clocks every system reaches for. Both tick in MILLISECONDS
// to match Phaser's `update(time, delta)` delta. Pure — no Phaser.

/**
 * A gate that becomes `ready` after a set duration elapses. Use for fire-rate
 * gates, attack cooldowns, dash cooldowns. Starts ready (left === 0).
 */
export class Cooldown {
  private left = 0;

  /** Arm the cooldown for `ms` (clamped ≥ 0). */
  set(ms: number): void {
    this.left = ms > 0 ? ms : 0;
  }

  /** Advance by `dt` ms. */
  tick(dt: number): void {
    if (this.left > 0) {
      this.left -= dt;
      if (this.left < 0) this.left = 0;
    }
  }

  /** True once the armed duration has fully elapsed. */
  get ready(): boolean {
    return this.left <= 0;
  }

  /** Remaining ms (0 when ready). */
  get remaining(): number {
    return this.left;
  }

  /** 0..1 progress toward ready, given the duration it was set with. */
  progress(durationMs: number): number {
    if (durationMs <= 0) return 1;
    const p = 1 - this.left / durationMs;
    return p < 0 ? 0 : p > 1 ? 1 : p;
  }
}

/**
 * A one-shot countdown. `tick(dt)` returns true on the frame it expires (and
 * stays expired thereafter until `reset`). Use for ripen windows, telegraphs,
 * timed objectives, i-frames.
 */
export class Timer {
  private left = 0;
  private armed = false;

  /** (Re)start the countdown for `ms`. */
  reset(ms: number): void {
    this.left = ms > 0 ? ms : 0;
    this.armed = this.left > 0;
  }

  /** Advance by `dt` ms; returns true exactly on the expiry frame. */
  tick(dt: number): boolean {
    if (!this.armed) return false;
    this.left -= dt;
    if (this.left <= 0) {
      this.left = 0;
      this.armed = false;
      return true;
    }
    return false;
  }

  /** Still counting down. */
  get running(): boolean {
    return this.armed;
  }

  /** Remaining ms. */
  get remaining(): number {
    return this.left;
  }
}
